import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listGoals,
  getGoal,
  createGoal,
  updateGoal,
  deleteGoal,
  listPlans,
  getPlan,
  createPlan,
  updatePlan,
  deletePlan,
  listActivities,
  getActivity,
  createActivity,
  updateActivity,
  completeActivity,
  deleteActivity,
  listCareerMoves,
  executeCareerMoveAction,
  type ListCareerMovesParams,
} from "@/api/career";
import type { CareerMoveActionKind } from "@career-ops-amplify/shared";
import { ApiError } from "@/api/client";

export function useGoals() {
  return useQuery({
    queryKey: ["goals"],
    queryFn: listGoals,
    staleTime: 30_000,
  });
}

export function useGoal(id: string | undefined) {
  return useQuery({
    queryKey: ["goals", id],
    queryFn: () => getGoal(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createGoal,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["goals"] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: Parameters<typeof updateGoal>[1] }) =>
      updateGoal(args.id, args.input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["goals"] });
      void qc.invalidateQueries({ queryKey: ["goals", vars.id] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteGoal,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["goals"] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function usePlans(goalId?: string) {
  return useQuery({
    queryKey: ["plans", goalId ?? "all"],
    queryFn: () => listPlans(goalId),
    staleTime: 30_000,
  });
}

export function usePlan(id: string | undefined) {
  return useQuery({
    queryKey: ["plans", "detail", id],
    queryFn: () => getPlan(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createPlan,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plans"] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: Parameters<typeof updatePlan>[1] }) =>
      updatePlan(args.id, args.input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["plans"] });
      void qc.invalidateQueries({ queryKey: ["plans", "detail", vars.id] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deletePlan,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plans"] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function useActivities(openOnly = false) {
  return useQuery({
    queryKey: ["activities", openOnly ? "open" : "all"],
    queryFn: () => listActivities(openOnly),
    staleTime: 30_000,
  });
}

export function useActivity(id: string | undefined) {
  return useQuery({
    queryKey: ["activities", "detail", id],
    queryFn: () => getActivity(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createActivity,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["activities"] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      input: Parameters<typeof updateActivity>[1];
    }) => updateActivity(args.id, args.input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["activities"] });
      void qc.invalidateQueries({ queryKey: ["activities", "detail", vars.id] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function useCompleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; outcome?: string }) =>
      completeActivity(args.id, args.outcome),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["activities"] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function useDeleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteActivity,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["activities"] });
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
  });
}

export function useCareerMoves(params?: ListCareerMovesParams) {
  return useQuery({
    queryKey: ["career-moves", params],
    queryFn: () => listCareerMoves(params),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export function useCareerMoveAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      action: CareerMoveActionKind | string;
      materialStateHash?: string;
      generationId?: string;
      reason?: string;
      payload?: Record<string, unknown>;
    }) =>
      executeCareerMoveAction(args.id, {
        action: args.action,
        fingerprint: args.id,
        materialStateHash: args.materialStateHash,
        generationId: args.generationId,
        reason: args.reason,
        payload: args.payload,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["career-moves"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        void qc.invalidateQueries({ queryKey: ["career-moves"] });
      }
    },
  });
}
